import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import RoleBasedNavigation from "../../components/ui/RoleBasedNavigation";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Select from "../../components/ui/Select";
import { Checkbox } from "../../components/ui/Checkbox";
import {
  projects as projectsAPI,
  users as usersAPI,
  appSettings as appSettingsAPI,
} from "../../lib/database";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { hashPassword, decryptPassword } from "../../lib/password";
import { useToast } from "../../components/ui/Toast";

const Admin = () => {
  const { user: currentUser } = useAuth();
  const { showToast } = useToast();
  const isSuperAdmin = currentUser?.role === "super_admin";
  const [activeTab, setActiveTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Projects state
  const [isAddingProject, setIsAddingProject] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectFormData, setProjectFormData] = useState({ name: "" });

  // Admin Users state
  const [isAddingAdminUser, setIsAddingAdminUser] = useState(false);
  const [editingAdminUser, setEditingAdminUser] = useState(null);
  const [adminUserFormData, setAdminUserFormData] = useState({
    name: "",
    unique_id: "",
    password: "",
    role: "project_admin",
    project_id: "",
  });
  const [generatedPassword, setGeneratedPassword] = useState(null);
  const [newUserUniqueId, setNewUserUniqueId] = useState(null);
  const [changePasswordUserId, setChangePasswordUserId] = useState(null);
  const [changePasswordNew, setChangePasswordNew] = useState("");
  const [changePasswordConfirm, setChangePasswordConfirm] = useState("");
  const [changePasswordSubmitting, setChangePasswordSubmitting] = useState(false);
  const [showingPasswordId, setShowingPasswordId] = useState(null);
  const [adminUserPasswords, setAdminUserPasswords] = useState({});
  const [loadingPassword, setLoadingPassword] = useState(null);
  const [geolocationEnforcementEnabled, setGeolocationEnforcementEnabled] =
    useState(true);
  const [savingGeoSetting, setSavingGeoSetting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch projects
      const projectsData = await projectsAPI.getAll();
      setProjects(projectsData || []);

      // Fetch admin users (super_admin and project_admin only) with project relations
      const allUsers = await usersAPI.getAll();
      const adminUsersData = (allUsers || []).filter(
        (user) =>
          user.role === "super_admin" || user.role === "project_admin"
      );
      setAdminUsers(adminUsersData || []);

      const geoSetting = await appSettingsAPI
        .getGeolocationEnforcement()
        .catch(() => true);
      setGeolocationEnforcementEnabled(geoSetting);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGeolocationRule = async () => {
    try {
      setSavingGeoSetting(true);
      setError(null);
      await appSettingsAPI.setGeolocationEnforcement(
        geolocationEnforcementEnabled,
      );
      showToast("Geolocation rule setting updated.", "success");
    } catch (err) {
      console.error("Error updating geolocation setting:", err);
      setError(err.message || "Failed to update geolocation setting");
    } finally {
      setSavingGeoSetting(false);
    }
  };

  // Projects handlers
  const handleAddProject = async () => {
    try {
      setError(null);
      if (!projectFormData.name?.trim()) {
        setError("Project name is required");
        return;
      }
      await projectsAPI.create({ name: projectFormData.name.trim() });
      await fetchData();
      setIsAddingProject(false);
      setProjectFormData({ name: "" });
    } catch (err) {
      console.error("Error creating project:", err);
      setError(err.message || "Failed to create project");
    }
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setProjectFormData({ name: project.name });
    setIsAddingProject(true);
  };

  const handleUpdateProject = async () => {
    try {
      setError(null);
      if (!projectFormData.name?.trim()) {
        setError("Project name is required");
        return;
      }
      await projectsAPI.update(editingProject.id, {
        name: projectFormData.name.trim(),
      });
      await fetchData();
      setIsAddingProject(false);
      setEditingProject(null);
      setProjectFormData({ name: "" });
    } catch (err) {
      console.error("Error updating project:", err);
      setError(err.message || "Failed to update project");
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this project? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      setError(null);
      await projectsAPI.delete(projectId);
      await fetchData();
    } catch (err) {
      console.error("Error deleting project:", err);
      setError(err.message || "Failed to delete project");
    }
  };

  // Admin Users handlers
  const generateRandomPassword = () => {
    const chars =
      "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
    let password = "";
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const handleAddAdminUser = async () => {
    try {
      setError(null);
      if (!adminUserFormData.name?.trim()) {
        setError("Name is required");
        return;
      }
      if (!adminUserFormData.role) {
        setError("Role is required");
        return;
      }
      if (adminUserFormData.role === "project_admin" && !adminUserFormData.project_id) {
        setError("Project is required for Project Admin");
        return;
      }

      const password = generateRandomPassword();
      // Generate unique ID using database function
      const { data: uniqueIdData, error: uniqueIdError } = await supabase.rpc(
        "generate_unique_user_id",
        { role_type: adminUserFormData.role }
      );
      
      let uniqueId;
      if (uniqueIdError || !uniqueIdData) {
        // Fallback: generate ID manually
        console.warn("RPC call failed, using fallback ID generation:", uniqueIdError);
        const existingUsers = await usersAPI.getByRole(adminUserFormData.role);
        const count = (existingUsers || []).length + 1;
        const prefix =
          adminUserFormData.role === "super_admin"
            ? "UA"
            : adminUserFormData.role === "project_admin"
            ? "UPA"
            : "UX";
        uniqueId = prefix + String(count).padStart(4, "0");
      } else {
        uniqueId = uniqueIdData;
      }

      const newUser = await usersAPI.create({
        unique_id: uniqueId,
        password: hashPassword(password),
        role: adminUserFormData.role,
        category: "admin",
        name: adminUserFormData.name.trim(),
        project_id:
          adminUserFormData.role === "project_admin"
            ? adminUserFormData.project_id
            : null,
      });

      setGeneratedPassword(password);
      setNewUserUniqueId(uniqueId);
      await fetchData();
      setAdminUserFormData({
        name: "",
        unique_id: "",
        password: "",
        role: "project_admin",
        project_id: "",
      });
      setIsAddingAdminUser(false);
    } catch (err) {
      console.error("Error creating admin user:", err);
      setError(err.message || "Failed to create admin user");
    }
  };

  const handleEditAdminUser = (user) => {
    setEditingAdminUser(user);
    setAdminUserFormData({
      name: user.name || "",
      unique_id: user.unique_id || "",
      password: "",
      role: user.role || "project_admin",
      project_id: user.project_id || "",
    });
    setIsAddingAdminUser(true);
  };

  const handleUpdateAdminUser = async () => {
    try {
      setError(null);
      if (!adminUserFormData.name?.trim()) {
        setError("Name is required");
        return;
      }

      const updateData = {
        name: adminUserFormData.name.trim(),
        role: adminUserFormData.role,
        project_id:
          adminUserFormData.role === "project_admin"
            ? adminUserFormData.project_id || null
            : null,
      };

      // Only update password if provided (store hashed)
      if (adminUserFormData.password?.trim()) {
        updateData.password = hashPassword(adminUserFormData.password.trim());
      }

      await usersAPI.update(editingAdminUser.id, updateData);
      await fetchData();
      setIsAddingAdminUser(false);
      setEditingAdminUser(null);
      setAdminUserFormData({
        name: "",
        unique_id: "",
        password: "",
        role: "project_admin",
        project_id: "",
      });
    } catch (err) {
      console.error("Error updating admin user:", err);
      setError(err.message || "Failed to update admin user");
    }
  };

  const handleDeleteAdminUser = async (userId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this admin user? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      setError(null);
      await usersAPI.delete(userId);
      await fetchData();
    } catch (err) {
      console.error("Error deleting admin user:", err);
      setError(err.message || "Failed to delete admin user");
    }
  };

  const handleShowPassword = async (userId) => {
    if (adminUserPasswords[userId] !== undefined) {
      setShowingPasswordId(showingPasswordId === userId ? null : userId);
      return;
    }
    try {
      setLoadingPassword(userId);
      const encrypted = await usersAPI.getPasswordById(userId);
      const plain = encrypted ? decryptPassword(encrypted) : null;
      setAdminUserPasswords((prev) => ({ ...prev, [userId]: plain }));
      setShowingPasswordId(userId);
    } catch (err) {
      console.error("Error fetching password:", err);
      setAdminUserPasswords((prev) => ({ ...prev, [userId]: null }));
      setShowingPasswordId(userId);
    } finally {
      setLoadingPassword(null);
    }
  };

  const handleOpenChangePassword = (userId) => {
    setChangePasswordUserId(userId);
    setChangePasswordNew("");
    setChangePasswordConfirm("");
  };

  const handleCloseChangePassword = () => {
    setChangePasswordUserId(null);
    setChangePasswordNew("");
    setChangePasswordConfirm("");
    setError(null);
  };

  const handleSubmitChangePassword = async () => {
    if (!changePasswordUserId) return;
    if (!changePasswordNew?.trim()) {
      setError("New password is required");
      return;
    }
    if (changePasswordNew.trim().length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (changePasswordNew !== changePasswordConfirm) {
      setError("Passwords do not match");
      return;
    }
    try {
      setError(null);
      setChangePasswordSubmitting(true);
      await usersAPI.update(changePasswordUserId, {
        password: hashPassword(changePasswordNew.trim()),
      });
      await fetchData();
      handleCloseChangePassword();
    } catch (err) {
      console.error("Error changing password:", err);
      setError(err.message || "Failed to change password");
    } finally {
      setChangePasswordSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Admin Management</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <RoleBasedNavigation />
        <main className="content-area">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="mb-6 md:mb-8">
              <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-2">
                Admin Management
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Manage projects and admin users
              </p>
              {error && (
                <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                  {error}
                </div>
              )}
            </div>

            {isSuperAdmin && (
              <div className="bg-card border border-border rounded-lg p-4 md:p-6 mb-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">
                      Geolocation Rule Enforcement
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Applies to survey and technical submission verification.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Checkbox
                      label="Enforce geolocation rule"
                      checked={geolocationEnforcementEnabled}
                      onChange={(e) =>
                        setGeolocationEnforcementEnabled(
                          e?.target?.checked || false,
                        )
                      }
                    />
                    <Button
                      variant="default"
                      onClick={handleSaveGeolocationRule}
                      loading={savingGeoSetting}
                      disabled={savingGeoSetting}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="bg-card border border-border rounded-lg mb-6">
              <div className="flex border-b border-border">
                <button
                  onClick={() => setActiveTab("projects")}
                  className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                    activeTab === "projects"
                      ? "text-primary border-b-2 border-primary bg-primary/5"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Icon name="Folder" size={18} />
                    <span>Projects</span>
                    <span className="ml-2 px-2 py-0.5 rounded-full bg-muted text-xs">
                      {projects?.length || 0}
                    </span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab("adminUsers")}
                  className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                    activeTab === "adminUsers"
                      ? "text-primary border-b-2 border-primary bg-primary/5"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Icon name="UserCog" size={18} />
                    <span>Admin Users</span>
                    <span className="ml-2 px-2 py-0.5 rounded-full bg-muted text-xs">
                      {adminUsers?.length || 0}
                    </span>
                  </div>
                </button>
              </div>
            </div>

            {/* Projects Tab */}
            {activeTab === "projects" && (
              <div className="bg-card border border-border rounded-lg p-4 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg md:text-xl font-semibold text-foreground">
                    Projects
                  </h2>
                  <Button
                    variant="default"
                    iconName="Plus"
                    onClick={() => {
                      setIsAddingProject(true);
                      setEditingProject(null);
                      setProjectFormData({ name: "" });
                    }}
                  >
                    Add Project
                  </Button>
                </div>

                {projects.length > 0 ? (
                  <div className="space-y-3">
                    {projects.map((project) => (
                      <div
                        key={project.id}
                        className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div>
                          <p className="font-medium text-foreground">
                            {project.name}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Created:{" "}
                            {new Date(project.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="Edit"
                            onClick={() => handleEditProject(project)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="Trash2"
                            onClick={() => handleDeleteProject(project.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">
                    No projects found. Create your first project.
                  </p>
                )}

                {/* Add/Edit Project Modal */}
                {isAddingProject && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
                    <div className="bg-card rounded-lg shadow-xl w-full max-w-md">
                      <div className="flex items-center justify-between p-6 border-b border-border">
                        <h2 className="text-xl font-semibold text-foreground">
                          {editingProject ? "Edit Project" : "Add Project"}
                        </h2>
                        <button
                          onClick={() => {
                            setIsAddingProject(false);
                            setEditingProject(null);
                            setProjectFormData({ name: "" });
                          }}
                          className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center"
                        >
                          <Icon name="X" size={20} />
                        </button>
                      </div>
                      <div className="p-6 space-y-4">
                        <Input
                          label="Project Name"
                          type="text"
                          value={projectFormData.name}
                          onChange={(e) =>
                            setProjectFormData({
                              ...projectFormData,
                              name: e.target.value,
                            })
                          }
                          placeholder="e.g., Solar Project 2025"
                          required
                        />
                        <div className="flex items-center justify-end gap-3">
                          <Button
                            variant="outline"
                            onClick={() => {
                              setIsAddingProject(false);
                              setEditingProject(null);
                              setProjectFormData({ name: "" });
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="default"
                            onClick={
                              editingProject
                                ? handleUpdateProject
                                : handleAddProject
                            }
                            disabled={!projectFormData.name?.trim()}
                          >
                            {editingProject ? "Update" : "Create"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Admin Users Tab */}
            {activeTab === "adminUsers" && (
              <div className="bg-card border border-border rounded-lg p-4 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg md:text-xl font-semibold text-foreground">
                    Admin Users
                  </h2>
                  <Button
                    variant="default"
                    iconName="Plus"
                    onClick={() => {
                      setIsAddingAdminUser(true);
                      setEditingAdminUser(null);
                      setAdminUserFormData({
                        name: "",
                        unique_id: "",
                        password: "",
                        role: "project_admin",
                        project_id: "",
                      });
                      setGeneratedPassword(null);
                      setNewUserUniqueId(null);
                    }}
                  >
                    Add Admin User
                  </Button>
                </div>

                {adminUsers.length > 0 ? (
                  <div className="space-y-3">
                    {adminUsers.map((user) => (
                      <div
                        key={user.id}
                        className="p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                <Icon name="User" size={20} className="text-primary" />
                              </div>
                              <div>
                                <h4 className="text-base font-semibold text-foreground">
                                  {user.name || "Unnamed User"}
                                </h4>
                                <p className="text-xs text-muted-foreground">
                                  {user.unique_id || "N/A"}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground ml-13">
                              <span>
                                {user.role === "super_admin"
                                  ? "Super Admin"
                                  : "Project Admin"}
                              </span>
                              {user.projects?.name && (
                                <>
                                  <span>•</span>
                                  <span>Project: {user.projects.name}</span>
                                </>
                              )}
                            </div>
                            {isSuperAdmin && user.role !== "super_admin" && showingPasswordId === user.id && adminUserPasswords[user.id] !== undefined && (
                              <div className="mt-3 ml-13 p-3 bg-muted rounded-lg border border-border">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-xs text-muted-foreground mb-1">Password</p>
                                    <p className="text-sm font-mono font-medium text-foreground">
                                      {adminUserPasswords[user.id] ?? "Cannot display (stored securely)"}
                                    </p>
                                  </div>
                                  {adminUserPasswords[user.id] && (
                                    <button
                                      onClick={() => navigator.clipboard.writeText(adminUserPasswords[user.id])}
                                      className="p-2 hover:bg-background rounded-lg transition-colors"
                                      title="Copy to clipboard"
                                    >
                                      <Icon name="Copy" size={16} className="text-muted-foreground" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {isSuperAdmin && user.role !== "super_admin" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                iconName={showingPasswordId === user.id ? "EyeOff" : "Eye"}
                                onClick={() => handleShowPassword(user.id)}
                                loading={loadingPassword === user.id}
                                disabled={loadingPassword === user.id}
                                title={showingPasswordId === user.id ? "Hide Password" : "Show Password"}
                              >
                                {showingPasswordId === user.id ? "Hide" : "Show"} Password
                              </Button>
                            )}
                            {isSuperAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                iconName="Key"
                                onClick={() => handleOpenChangePassword(user.id)}
                                title="Change Password"
                              >
                                Change Password
                              </Button>
                            )}
                            {user.role !== "super_admin" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                iconName="Edit"
                                onClick={() => handleEditAdminUser(user)}
                              >
                                Edit
                              </Button>
                            )}
                            {user.role !== "super_admin" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                iconName="Trash2"
                                onClick={() => handleDeleteAdminUser(user.id)}
                              >
                                Delete
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">
                    No admin users found.
                  </p>
                )}

                {/* Add/Edit Admin User Modal */}
                {isAddingAdminUser && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
                    <div className="bg-card rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                      <div className="flex items-center justify-between p-6 border-b border-border">
                        <h2 className="text-xl font-semibold text-foreground">
                          {editingAdminUser
                            ? "Edit Admin User"
                            : "Add Admin User"}
                        </h2>
                        <button
                          onClick={() => {
                            setIsAddingAdminUser(false);
                            setEditingAdminUser(null);
                            setAdminUserFormData({
                              name: "",
                              unique_id: "",
                              password: "",
                              role: "project_admin",
                              project_id: "",
                            });
                            setGeneratedPassword(null);
                            setNewUserUniqueId(null);
                          }}
                          className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center"
                        >
                          <Icon name="X" size={20} />
                        </button>
                      </div>
                      <div className="p-6 space-y-4">
                        {/* Show generated credentials for new users */}
                        {generatedPassword && newUserUniqueId && (
                          <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
                            <p className="text-sm font-medium text-foreground mb-2">
                              User Created Successfully!
                            </p>
                            <div className="space-y-2 text-sm">
                              <div>
                                <span className="text-muted-foreground">
                                  User ID:{" "}
                                </span>
                                <span className="font-mono font-medium text-foreground">
                                  {newUserUniqueId}
                                </span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">
                                  Password:{" "}
                                </span>
                                <span className="font-mono font-medium text-foreground">
                                  {generatedPassword}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}

                        <Input
                          label="Name"
                          type="text"
                          value={adminUserFormData.name}
                          onChange={(e) =>
                            setAdminUserFormData({
                              ...adminUserFormData,
                              name: e.target.value,
                            })
                          }
                          placeholder="Enter user name"
                          required
                        />

                        <Select
                          label="Role"
                          options={[
                            {
                              value: "super_admin",
                              label: "Super Admin",
                            },
                            {
                              value: "project_admin",
                              label: "Project Admin",
                            },
                          ]}
                          value={adminUserFormData.role}
                          onChange={(value) =>
                            setAdminUserFormData({
                              ...adminUserFormData,
                              role: value,
                              project_id:
                                value === "super_admin" ? "" : adminUserFormData.project_id,
                            })
                          }
                          required
                          disabled={!!editingAdminUser}
                        />

                        {adminUserFormData.role === "project_admin" && (
                          <Select
                            label="Project"
                            options={[
                              { value: "", label: "Select Project" },
                              ...(projects.map((p) => ({
                                value: p.id,
                                label: p.name,
                              })) || []),
                            ]}
                            value={adminUserFormData.project_id}
                            onChange={(value) =>
                              setAdminUserFormData({
                                ...adminUserFormData,
                                project_id: value,
                              })
                            }
                            required
                          />
                        )}

                        {editingAdminUser && isSuperAdmin && (
                          <Input
                            label="New Password (leave blank to keep current)"
                            type="password"
                            value={adminUserFormData.password}
                            onChange={(e) =>
                              setAdminUserFormData({
                                ...adminUserFormData,
                                password: e.target.value,
                              })
                            }
                            placeholder="Enter new password"
                          />
                        )}

                        <div className="flex items-center justify-end gap-3 pt-4">
                          <Button
                            variant="outline"
                            onClick={() => {
                              setIsAddingAdminUser(false);
                              setEditingAdminUser(null);
                              setAdminUserFormData({
                                name: "",
                                unique_id: "",
                                password: "",
                                role: "project_admin",
                                project_id: "",
                              });
                              setGeneratedPassword(null);
                              setNewUserUniqueId(null);
                            }}
                          >
                            {generatedPassword ? "Close" : "Cancel"}
                          </Button>
                          {!generatedPassword && (
                            <Button
                              variant="default"
                              onClick={
                                editingAdminUser
                                  ? handleUpdateAdminUser
                                  : handleAddAdminUser
                              }
                              disabled={
                                !adminUserFormData.name?.trim() ||
                                !adminUserFormData.role ||
                                (adminUserFormData.role === "project_admin" &&
                                  !adminUserFormData.project_id)
                              }
                            >
                              {editingAdminUser ? "Update" : "Create"}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Change Password Modal (super_admin only) */}
                {changePasswordUserId && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
                    <div className="bg-card rounded-lg shadow-xl w-full max-w-md">
                      <div className="flex items-center justify-between p-6 border-b border-border">
                        <h2 className="text-xl font-semibold text-foreground">
                          Change Password
                        </h2>
                        <button
                          onClick={handleCloseChangePassword}
                          className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center"
                          disabled={changePasswordSubmitting}
                        >
                          <Icon name="X" size={20} />
                        </button>
                      </div>
                      <div className="p-6 space-y-4">
                        <Input
                          label="New Password"
                          type="password"
                          value={changePasswordNew}
                          onChange={(e) => setChangePasswordNew(e.target.value)}
                          placeholder="Enter new password"
                          required
                        />
                        <Input
                          label="Confirm Password"
                          type="password"
                          value={changePasswordConfirm}
                          onChange={(e) => setChangePasswordConfirm(e.target.value)}
                          placeholder="Confirm new password"
                          required
                        />
                        {error && (
                          <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                            {error}
                          </div>
                        )}
                        <div className="flex items-center justify-end gap-3 pt-2">
                          <Button
                            variant="outline"
                            onClick={handleCloseChangePassword}
                            disabled={changePasswordSubmitting}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="default"
                            onClick={handleSubmitChangePassword}
                            loading={changePasswordSubmitting}
                            disabled={
                              !changePasswordNew?.trim() ||
                              !changePasswordConfirm?.trim() ||
                              changePasswordSubmitting
                            }
                          >
                            Update Password
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default Admin;
